import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import {
  GrauSemente,
  TipoDano,
  TipoExigenciaDano,
  IntensidadeDano,
  DanoSelecionado,
  FormValues,
} from '../components/FormularioTetrazolio/FormularioTetrazolioClassificacao/types';
import type useClassificacaoSementes from './useClassificacaoSementes';
import type useTetrazolioConfiguracoes from './useTetrazolioConfiguracoes';

const validationSchema = Yup.object({
  grauSelecionado: Yup.number()
    .required('Selecione um grau')
    .oneOf(
      Object.values(GrauSemente).filter(
        (v) => typeof v === 'number',
      ) as number[],
      'Grau inválido',
    ),
  danosSelecionados: Yup.array()
    .of(
      Yup.object({
        tipo: Yup.string().required(),
        intensidade: Yup.string().required(),
      }),
    )
    .default([]),
});

interface UseFormularioClassificacaoProps {
  classificacao: ReturnType<typeof useClassificacaoSementes>;
  configuracoes: ReturnType<typeof useTetrazolioConfiguracoes>;
}

interface UseFormularioClassificacaoResult {
  formik: any;
  handleGrauClick: (grauId: string) => void;
  handleDanoClick: (tipoDano: TipoDano) => void;
  getDanoButtonClass: (tipoDano: TipoDano) => string;
  getGrauButtonClass: (grauId: string) => string;
  grauConfig: any;
  erro: string | null;
  podelancar: boolean;
  modalDanoIntensoVisible: boolean;
  fecharModalDanoIntenso: () => void;
}

export default function useFormularioClassificacao({
  classificacao,
  configuracoes,
}: UseFormularioClassificacaoProps): UseFormularioClassificacaoResult {
  const { graus } = configuracoes;
  const { adicionarLancamento, validarLancamento } = classificacao;
  const [modalDanoIntensoVisible, setModalDanoIntensoVisible] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      grauSelecionado: null,
      grauIdSelecionado: null,
      danosSelecionados: [],
    },
    validationSchema,
    onSubmit: (values) => {
      try {
        const grauConfig = graus.find((grau) => grau.id === values.grauIdSelecionado);

        if (grauConfig?.exigenciaDano === TipoExigenciaDano.INTENSO) {
          const temDanoIntenso = values.danosSelecionados.some(
            (dano) => dano.intensidade === IntensidadeDano.INTENSO,
          );

          if (!temDanoIntenso) {
            setModalDanoIntensoVisible(true);
            return;
          }
        }

        adicionarLancamento(values);
        formik.setFieldValue('grauSelecionado', null);
        formik.setFieldValue('grauIdSelecionado', null);
        formik.setFieldValue('danosSelecionados', []);
      } catch (error) {
        console.error('Erro ao lançar semente:', error);
      }
    },
  });

  const handleGrauClick = (grauId: string) => {
    const grauConfig = graus.find((grau) => grau.id === grauId);
    formik.setFieldValue('grauSelecionado', grauConfig?.grau ?? null);
    formik.setFieldValue('grauIdSelecionado', grauId);
    formik.setFieldValue('danosSelecionados', []);
  };

  const handleDanoClick = (tipoDano: TipoDano) => {
    const grauConfig = graus.find(
      (grau) => grau.id === formik.values.grauIdSelecionado,
    );
    const danoExistente = formik.values.danosSelecionados.find(
      (dano) => dano.tipo === tipoDano,
    );

    // Graus que não usam dano (ex: Grau 1)
    if (grauConfig?.exigenciaDano === TipoExigenciaDano.NENHUM) {
      return;
    }

    if (!danoExistente) {
      // Primeira vez clicando: adiciona como NORMAL
      const novoDano: DanoSelecionado = {
        tipo: tipoDano,
        intensidade: IntensidadeDano.NORMAL,
      };
      formik.setFieldValue('danosSelecionados', [
        ...formik.values.danosSelecionados,
        novoDano,
      ]);
    } else if (danoExistente.intensidade === IntensidadeDano.NORMAL) {
      // Segunda vez clicando: transforma em INTENSO
      // Permitir intenso para graus 2, 3, 4, 5 (NORMAL) e 6, 7, 8 (INTENSO)
      const danoAtualizado = {
        ...danoExistente,
        intensidade: IntensidadeDano.INTENSO,
      };
      formik.setFieldValue(
        'danosSelecionados',
        formik.values.danosSelecionados.map((d) => (d.tipo === tipoDano ? danoAtualizado : d)),
      );
    } else {
      // Terceira vez clicando: remove o dano
      formik.setFieldValue(
        'danosSelecionados',
        formik.values.danosSelecionados.filter((d) => d.tipo !== tipoDano),
      );
    }
  };

  const getDanoButtonClass = (tipoDano: TipoDano) => {
    const dano = formik.values.danosSelecionados.find(
      (d) => d.tipo === tipoDano,
    );
    if (!dano) return 'dano-button';
    return `dano-button ${
      dano.intensidade === IntensidadeDano.INTENSO ? 'intenso' : 'normal'
    }`;
  };

  const getGrauButtonClass = (grauId: string) => {
    const config = graus.find((grau) => grau.id === grauId);
    let exigenciaClass = '';

    if (config?.exigenciaDano === TipoExigenciaDano.NENHUM) {
      exigenciaClass = 'nao-usa-dano';
    } else if (config?.exigenciaDano === TipoExigenciaDano.INTENSO) {
      exigenciaClass = 'exige-dano-intenso';
    } else if (config?.exigenciaDano === TipoExigenciaDano.NORMAL) {
      exigenciaClass = 'exige-dano';
    }

    return `grau-button grau-${config?.grau} ${exigenciaClass} ${
      formik.values.grauIdSelecionado === grauId ? 'selected' : ''
    }`;
  };

  const grauConfig = formik.values.grauIdSelecionado
    ? graus.find((grau) => grau.id === formik.values.grauIdSelecionado)
    : null;

  const erro = formik.values.grauSelecionado !== null
    ? validarLancamento(formik.values)
    : null;

  const podelancar = formik.values.grauSelecionado !== null && !erro;

  const fecharModalDanoIntenso = () => {
    setModalDanoIntensoVisible(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Enter' && podelancar) {
        e.preventDefault();
        formik.handleSubmit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [podelancar, formik]);

  return {
    formik,
    handleGrauClick,
    handleDanoClick,
    getDanoButtonClass,
    getGrauButtonClass,
    grauConfig,
    erro,
    podelancar,
    modalDanoIntensoVisible,
    fecharModalDanoIntenso,
  };
}
